import { Building2, Check, Plus } from "lucide-react";
import { useNavigate } from "react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";
import { BrandIcon } from "@/components/shell/BrandIcon";
import { cn } from "@/lib/utils";
import {
  useOptionalAccountWorkspace,
  type WorkspaceBrand,
} from "@/lib/account-workspace";
import { ContextHeaderTrigger } from "./ContextHeaderTrigger";

export type BrandContextSwitcherProps = {
  brand: WorkspaceBrand | null;
  /** App name when in sub-app panel; omit on brand hub → subtitle defaults to 品牌运营. */
  appTitle?: string | null;
};

/** Brand / app-level context header: brand identity + subtitle, switch brands or create brand. */
export function BrandContextSwitcher({ brand, appTitle }: BrandContextSwitcherProps) {
  const workspace = useOptionalAccountWorkspace();
  const { isMobile, setOpenMobile } = useSidebar();
  const navigate = useNavigate();

  if (!workspace?.accountId) return null;

  const { accountId, brands, accessKind, openCreateBrand } = workspace;
  const base = `/accounts/${accountId}`;
  const title = brand?.name ?? "选择品牌";
  const subtitle = appTitle?.trim() || "品牌运营";
  const canCreateBrand = accessKind === "membership" && Boolean(openCreateBrand);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ContextHeaderTrigger
          aria-label={appTitle ? "切换品牌或应用上下文" : "切换品牌"}
          title={title}
          subtitle={subtitle}
          icon={
            brand ? (
              <BrandIcon
                name={brand.name}
                iconKey={brand.icon_key}
                color={brand.color}
                size="lg"
                className="size-8 rounded-lg"
              />
            ) : (
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                <Building2 className="size-4 text-muted-foreground" />
              </span>
            )
          }
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-(--radix-dropdown-menu-trigger-width) min-w-64"
        align="start"
        side={isMobile ? "bottom" : "right"}
      >
        <DropdownMenuLabel className="text-xs text-muted-foreground">品牌</DropdownMenuLabel>
        {brands.map((item) => {
          const active = brand != null && item.id === brand.id;
          return (
            <DropdownMenuItem
              key={item.id}
              className={cn("cursor-pointer", active && "bg-accent")}
              onSelect={() => {
                setOpenMobile(false);
                navigate(`${base}/brands/${item.id}`);
              }}
            >
              <BrandIcon
                name={item.name}
                iconKey={item.icon_key}
                color={item.color}
                size="sm"
              />
              <span className="min-w-0 flex-1 truncate font-heading">{item.name}</span>
              {active ? <Check className="size-4 shrink-0" /> : null}
            </DropdownMenuItem>
          );
        })}
        {brands.length === 0 ? (
          <DropdownMenuItem className="pointer-events-none opacity-60">
            <span>暂无品牌</span>
          </DropdownMenuItem>
        ) : null}
        {canCreateBrand ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={() => {
                setOpenMobile(false);
                openCreateBrand();
              }}
            >
              <Plus className="size-4" />
              <span className="min-w-0 flex-1 truncate font-heading">创建新品牌</span>
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
